import React, { useEffect, useRef, useState } from "react";
import { Newspaper, ChevronLeft, ChevronRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getPerView = () => {
  if (typeof window === "undefined") return 3;
  const w = window.innerWidth;
  if (w < 640) return 1;
  if (w < 1024) return 2;
  return 3;
};

const NewsFeed = () => {
  const [items, setItems] = useState([]);
  const [perView, setPerView] = useState(getPerView());
  const [index, setIndex] = useState(0);
  const [animate, setAnimate] = useState(true);
  const [resetKey, setResetKey] = useState(0);
  const pausedRef = useRef(false);

  // Fetch news (backend caches & refreshes every 30 min).
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const res = await fetch(`${API}/news?limit=5`);
        if (!res.ok) return;
        const data = await res.json();
        if (active && data && Array.isArray(data.items)) setItems(data.items);
      } catch (e) {
        /* ignore transient errors */
      }
    };
    load();
    const id = setInterval(load, 30 * 60 * 1000);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, []);

  useEffect(() => {
    const onResize = () => setPerView(getPerView());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    setAnimate(false);
    setIndex(0);
  }, [perView]);

  const total = items.length;
  const stepPct = 100 / perView;
  const canLoop = total > perView;
  const slides = canLoop ? [...items, ...items.slice(0, perView)] : items;

  // Auto-advance one position to the left every 10s (resets on manual nav).
  useEffect(() => {
    if (!canLoop) return;
    const id = setInterval(() => {
      if (pausedRef.current) return;
      setAnimate(true);
      setIndex((i) => i + 1);
    }, 10000);
    return () => clearInterval(id);
  }, [canLoop, resetKey]);

  // Re-enable the transition after a silent loop reset / jump.
  useEffect(() => {
    if (!animate) {
      const raf = requestAnimationFrame(() => setAnimate(true));
      return () => cancelAnimationFrame(raf);
    }
  }, [animate]);

  const handleTransitionEnd = () => {
    if (canLoop && index >= total) {
      setAnimate(false);
      setIndex(0);
    }
  };

  const goNext = () => {
    if (!canLoop) return;
    setResetKey((k) => k + 1);
    setAnimate(true);
    setIndex((i) => i + 1);
  };

  const goPrev = () => {
    if (!canLoop) return;
    setResetKey((k) => k + 1);
    if (index <= 0) {
      // jump to the cloned tail without animation, then slide back one
      setAnimate(false);
      setIndex(total);
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          setAnimate(true);
          setIndex(total - 1);
        })
      );
    } else {
      setAnimate(true);
      setIndex((i) => i - 1);
    }
  };

  return (
    <section className="relative pt-10 pb-10">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <span className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-white/80">
              <Newspaper className="h-4 w-4" /> Actueel
            </span>
            <h2 className="mt-2 font-heading text-4xl font-extrabold text-white">Het laatste nieuws</h2>
          </div>
          <div className="flex items-center gap-4">
            {canLoop && (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={goPrev}
                  aria-label="Vorige"
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-white/25 bg-white/15 text-white transition-all hover:bg-white hover:text-[#1E6BE6] active:scale-95"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  aria-label="Volgende"
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-white/25 bg-white/15 text-white transition-all hover:bg-white hover:text-[#1E6BE6] active:scale-95"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        </div>

        {total === 0 ? (
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-80 animate-pulse rounded-3xl bg-[#4288F5]/60 border border-white/10" />
            ))}
          </div>
        ) : (
          // py gives the card shadows room so they aren't clipped by overflow-hidden
          <div
            className="mt-8 overflow-x-clip"
            onMouseEnter={() => { pausedRef.current = true; }}
            onMouseLeave={() => { pausedRef.current = false; }}
          >
            <div
              className="flex items-stretch"
              style={{
                transform: `translateX(-${index * stepPct}%)`,
                transition: animate ? "transform 700ms cubic-bezier(0.22,1,0.36,1)" : "none",
              }}
              onTransitionEnd={handleTransitionEnd}
            >
              {slides.map((n, i) => (
                <div
                  key={`${n.link}-${i}`}
                  className="shrink-0 px-2.5"
                  style={{ flexBasis: `${stepPct}%`, maxWidth: `${stepPct}%` }}
                >
                  <div
                    data-testid="news-card"
                    className="flex h-full flex-col overflow-hidden rounded-3xl bg-[#4288F5] border border-white/15 shadow-[0_12px_26px_-16px_rgba(8,30,80,0.45)]"
                  >
                    <div className="flex flex-1 flex-col p-5">
                      <h3 className="font-heading text-lg font-extrabold leading-snug text-white">
                        {n.title}
                      </h3>
                      {n.summary ? (
                        <p className="mt-2 text-sm leading-relaxed text-white/85">{n.summary}</p>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default NewsFeed;
